import type {
  Environment,
  CreateEnvironmentRequest,
  GitHubUser,
  ApiResponse,
  ListResponse,
  Database,
} from '@shared/types';
import type { Job } from '../components/JobTable';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://pk8s-api.aops.dev';

// Auth session type
export interface AuthSession {
  user: GitHubUser | null;
  session: { id: string; expiresAt: string } | null;
}

// Auth client
export const authClient = {
  signInWithGitHub: () => {
    window.location.href = `${API_BASE_URL}/api/auth/github`;
  },

  signOut: async (): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/api/auth/signout`, {
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to sign out');
    }
  },

  getSession: async (): Promise<AuthSession> => {
    const response = await fetch(`${API_BASE_URL}/api/auth/session`, {
      credentials: 'include',
    });

    if (!response.ok) {
      return { user: null, session: null };
    }

    return response.json();
  },
};

// API client class
class ApiClient {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    const response = await fetch(url, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...options?.headers,
      },
      ...options,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.error?.message || `HTTP ${response.status}`);
    }

    const data: ApiResponse<T> = await response.json();
    return data.data as T;
  }

  // Environment endpoints
  async listEnvironments(params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<ListResponse<Environment>> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());

    const query = searchParams.toString();
    const endpoint = `/api/environments${query ? `?${query}` : ''}`;

    return this.request(endpoint);
  }

  async getEnvironment(id: string): Promise<Environment> {
    return this.request(`/api/environments/${id}`);
  }

  async createEnvironment(params: CreateEnvironmentRequest): Promise<Environment> {
    return this.request('/api/environments', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async deleteEnvironment(id: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/api/environments/${id}`, {
      method: 'DELETE',
    });
  }

  async retryEnvironment(id: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/api/environments/${id}/retry`, {
      method: 'POST',
    });
  }

  async getEnvironmentResources(id: string): Promise<any> {
    return this.request(`/api/environments/${id}/resources`);
  }

  async extendEnvironmentTTL(id: string, ttl: string): Promise<Environment> {
    return this.request(`/api/environments/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ ttl }),
    });
  }

  // Database endpoints
  async listDatabases(params?: {
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<ListResponse<any>> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.offset) searchParams.set('offset', params.offset.toString());

    const query = searchParams.toString();
    const endpoint = `/api/databases${query ? `?${query}` : ''}`;

    const response = await this.request<{ databases: any[] }>(endpoint);
    return { 
      items: response.databases, 
      total: response.databases.length, 
      limit: params?.limit || 50, 
      offset: params?.offset || 0 
    };
  }

  async getDatabase(id: string): Promise<any> {
    return this.request(`/api/databases/${id}`);
  }

  async listDatabaseSnapshots(): Promise<ListResponse<any>> {
    const response = await this.request<{ snapshots: any[]; latest: string }>('/api/databases/snapshots');
    return { 
      items: response.snapshots, 
      total: response.snapshots.length, 
      limit: 50, 
      offset: 0 
    };
  }

  async createDatabase(params: {
    snapshot?: string;
    ttl_hours?: number;
  }): Promise<any> {
    return this.request('/api/databases', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async deleteDatabase(id: string): Promise<{ success: boolean; message: string }> {
    return this.request(`/api/databases/${id}`, {
      method: 'DELETE',
    });
  }

  async getDatabaseJobs(id: string): Promise<any[]> {
    const response = await fetch(`${API_BASE_URL}/api/databases/${id}/jobs`, {
      credentials: 'include'
    });
    if (!response.ok) throw new Error('Failed to fetch database jobs');
    const data = await response.json();
    return data.data.jobs;
  }

  async extendDatabaseTTL(id: string, ttl: string): Promise<any> {
    return this.request(`/api/databases/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ ttl }),
    });
  }

  // GitHub endpoints
  async listGitHubRepositories(): Promise<ListResponse<any>> {
    return this.request('/github/repositories');
  }

  async listGitHubBranches(owner: string, repo: string): Promise<ListResponse<any>> {
    return this.request(`/github/repositories/${owner}/${repo}/branches`);
  }

  async getRepositoryConfig(owner: string, repo: string, branch: string): Promise<any> {
    return this.request(`/github/repositories/${owner}/${repo}/config?branch=${branch}`);
  }

  async getClusterMetadata(): Promise<any> {
    const response = await this.request('/api/cluster/metadata') as any;
    // Note: this.request() already extracts .data from the API response
    return {
      externalDnsDomains: response.domains?.externalDns || [],
      defaultTTL: response.ttl?.default || '7d',
      maxTTL: response.ttl?.max || '30d',
      certIssuers: response.domains?.certIssuers || [],
      namespacePrefix: response.namespace?.prefix || 'pk8s-',
    };
  }

  // =============================================================================
  // API KEY MANAGEMENT
  // =============================================================================

  async getApiKeys(): Promise<any> {
    return this.request('/api/api-keys');
  }

  async getApiKeyScopes(): Promise<any> {
    return this.request('/api/api-keys/scopes');
  }

  async createApiKey(data: {
    name: string;
    description?: string;
    expires_at?: string;
    scopes?: string[];
  }): Promise<any> {
    return this.request('/api/api-keys', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteApiKey(id: string): Promise<any> {
    return this.request(`/api/api-keys/${id}`, {
      method: 'DELETE',
    });
  }

  // =============================================================================
  // ADMIN QUEUE MANAGEMENT
  // =============================================================================

  async getAdminQueueHistory(params?: {
    limit?: number;
    offset?: number;
    job_type?: string;
    status?: string;
    user_id?: string;
  }): Promise<any> {
    const query = new URLSearchParams(
      Object.entries(params || {})
        .filter(([_, v]) => v !== undefined && v !== '')
        .map(([k, v]) => [k, String(v)])
    );
    // Don't use this.request() to preserve the full response (data + count)
    const url = `${API_BASE_URL}/api/admin/queue-history?${query}`;
    const response = await fetch(url, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.error?.message || `HTTP ${response.status}`);
    }
    return response.json(); // Return full response including count
  }

  async getAdminQueueMetrics(): Promise<any> {
    return this.request('/api/admin/queue-metrics');
  }

  // =============================================================================
  // ADMIN STATS
  // =============================================================================

  async getSystemHealth(): Promise<any> {
    // Health endpoints return data directly without API wrapper
    const response = await fetch(`${API_BASE_URL}/health/deps`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    return response.json();
  }

  async getStuckEnvironments(): Promise<any> {
    return this.request('/api/admin/stuck-environments');
  }

  async getCleanupStats(): Promise<any> {
    return this.request('/api/admin/cleanup-stats');
  }

  // Health check methods
  async getServiceHealth(services: string[]): Promise<any[]> {
    const query = new URLSearchParams({ services: services.join(',') });
    return this.request(`/health/service-status?${query}`);
  }

  async recheckServiceHealth(): Promise<void> {
    await this.request('/health/admin/refresh', {
      method: 'POST'
    });
  }

  // =============================================================================
  // ADMIN RECONCILIATION
  // =============================================================================

  async startReconciliation(): Promise<{ jobId: string; message: string }> {
    const response = await fetch(`${API_BASE_URL}/api/admin/reconcile`, {
      method: 'GET',
      credentials: 'include',
    });
    const data = await response.json();
    return data.data;
  }

  async getJob(jobId: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/api/jobs/${jobId}`, {
      credentials: 'include',
    });
    const data = await response.json();
    return data.data;
  }

  async importDatabase(databaseId: string, dbClonerData: any): Promise<{ message: string; id: string }> {
    return this.request('/api/admin/reconcile/import', {
      method: 'POST',
      body: JSON.stringify({ databaseId, dbClonerData }),
    });
  }

  async syncDatabase(databaseId: string, dbClonerData: any): Promise<{ message: string }> {
    return this.request('/api/admin/reconcile/sync', {
      method: 'POST',
      body: JSON.stringify({ databaseId, dbClonerData }),
    });
  }

  async deleteDatabaseFromPortal(databaseId: string): Promise<{ message: string }> {
    return this.request('/api/admin/reconcile/delete', {
      method: 'POST',
      body: JSON.stringify({ databaseId }),
    });
  }

  async deleteDatabaseFromDbCloner(databaseName: string): Promise<{ message: string; jobId: string }> {
    return this.request('/api/admin/reconcile/delete-from-db-cloner', {
      method: 'POST',
      body: JSON.stringify({ databaseName }),
    });
  }

  async getCachedReconciliation(): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/api/admin/reconcile/cached`, {
      credentials: 'include',
    });
    const data = await response.json();
    return data.data;
  }

  async getReconciliationJobs(): Promise<Job[]> {
    const response = await fetch(`${API_BASE_URL}/api/admin/reconcile/jobs`, {
      credentials: 'include',
    });
    const data = await response.json();
    return data.data;
  }
}

// Export singleton instance
export const api = new ApiClient();

